import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek,
  isSameDay, isSameMonth, format, addMonths, subMonths, parseISO
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '@/context/AuthContext';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, MapPin, User as UserIcon, Clock, FileText, ExternalLink } from 'lucide-react';

const STATUS_META = {
  envoyee:   { label: 'Envoyée',  color: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500' },
  acceptee:  { label: 'Acceptée', color: 'bg-blue-100 text-blue-800 border-blue-300',   dot: 'bg-blue-500'  },
  validee:   { label: 'Acceptée', color: 'bg-blue-100 text-blue-800 border-blue-300',   dot: 'bg-blue-500'  },
  en_cours:  { label: 'En cours', color: 'bg-blue-100 text-blue-800 border-blue-300',   dot: 'bg-blue-500'  },
  terminee:  { label: 'Terminée', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500' },
  expiree:   { label: 'Expirée',  color: 'bg-red-100 text-red-700 border-red-300',     dot: 'bg-red-500'    },
  probleme:  { label: 'Problème', color: 'bg-red-100 text-red-700 border-red-300',     dot: 'bg-red-500'    },
};

const FILTERS = [
  { key: 'all',       label: 'Toutes' },
  { key: 'envoyee',   label: 'Envoyées' },
  { key: 'acceptee',  label: 'Acceptées' },
  { key: 'terminee',  label: 'Terminées' },
];

function getEventDate(mission) {
  // Prefer the artisan-chosen slot, else the deadline (so unassigned missions still appear).
  const iso = mission.scheduled_at || mission.deadline_at;
  return iso ? parseISO(iso) : null;
}

export const CalendarPage = () => {
  const { token } = useAuth();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(new Date()); // first day of the visible month
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null); // mission shown in the modal

  useEffect(() => {
    let cancelled = false;
    const fetchMissions = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/missions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) setMissions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        // silent — empty state will be shown
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) fetchMissions();
    return () => { cancelled = true; };
  }, [token]);

  const filteredMissions = useMemo(() => {
    if (filter === 'all') return missions;
    return missions.filter(m => {
      if (filter === 'acceptee') return ['acceptee', 'validee', 'en_cours'].includes(m.status);
      return m.status === filter;
    });
  }, [missions, filter]);

  // Bucket missions by yyyy-mm-dd for fast per-day lookup
  const missionsByDay = useMemo(() => {
    const buckets = new Map();
    for (const m of filteredMissions) {
      const dt = getEventDate(m);
      if (!dt) continue;
      const key = format(dt, 'yyyy-MM-dd');
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(m);
    }
    return buckets;
  }, [filteredMissions]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const counts = useMemo(() => {
    const c = { all: missions.length, envoyee: 0, acceptee: 0, terminee: 0 };
    for (const m of missions) {
      if (m.status === 'envoyee') c.envoyee += 1;
      else if (['acceptee', 'validee', 'en_cours'].includes(m.status)) c.acceptee += 1;
      else if (m.status === 'terminee') c.terminee += 1;
    }
    return c;
  }, [missions]);

  return (
    <MainLayout>
      <Header title="Calendrier des missions" subtitle="Vue mensuelle des interventions assignées" />

      <div className="p-6 space-y-4">

        {/* Filters + month nav */}
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map(f => {
              const active = filter === f.key;
              const count = counts[f.key];
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    active ? 'bg-[#FF5A6E] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}{count != null ? ` (${count})` : ''}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Aujourd'hui</Button>
            <button
              onClick={() => setCursor(c => subMonths(c, 1))}
              className="p-2 rounded-md border border-slate-200 hover:bg-slate-50"
              aria-label="Mois précédent"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="min-w-[160px] text-center text-sm font-semibold text-slate-900 capitalize">
              {format(cursor, 'MMMM yyyy', { locale: fr })}
            </div>
            <button
              onClick={() => setCursor(c => addMonths(c, 1))}
              className="p-2 rounded-md border border-slate-200 hover:bg-slate-50"
              aria-label="Mois suivant"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Calendar grid */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {/* Week header */}
          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
              <div key={d} className="px-2 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {days.map((day, idx) => {
              const inMonth = isSameMonth(day, cursor);
              const isToday = isSameDay(day, new Date());
              const dayKey = format(day, 'yyyy-MM-dd');
              const evts = missionsByDay.get(dayKey) || [];
              return (
                <div
                  key={idx}
                  className={`min-h-[100px] p-1.5 border-b border-r border-slate-100 ${
                    inMonth ? 'bg-white' : 'bg-slate-50/40 text-slate-400'
                  }`}
                >
                  <div className={`text-[11px] font-semibold mb-1 inline-block px-1.5 py-0.5 rounded ${
                    isToday ? 'bg-[#FF5A6E] text-white' : (inMonth ? 'text-slate-700' : 'text-slate-400')
                  }`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1">
                    {evts.slice(0, 3).map(m => {
                      const meta = STATUS_META[m.status] || { color: 'bg-slate-100 text-slate-700 border-slate-300' };
                      const dt = getEventDate(m);
                      const time = dt ? format(dt, 'HH:mm') : '';
                      return (
                        <button
                          key={m.id}
                          onClick={() => setSelected(m)}
                          className={`w-full text-left text-[11px] px-1.5 py-1 rounded border ${meta.color} truncate hover:shadow-sm transition`}
                          title={`${m.property_name} — ${time}`}
                        >
                          <span className="font-semibold mr-1">{time}</span>
                          {m.property_name || 'Mission'}
                        </button>
                      );
                    })}
                    {evts.length > 3 && (
                      <button
                        onClick={() => setSelected(evts[3])}
                        className="text-[10px] text-slate-500 hover:text-slate-700 pl-1.5"
                      >
                        +{evts.length - 3} autre{evts.length - 3 > 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {loading && (
          <div className="text-center text-slate-400 text-sm py-4">Chargement...</div>
        )}
        {!loading && missions.length === 0 && (
          <div className="text-center text-slate-400 text-sm py-4">
            Aucune mission assignée pour le moment.
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 pt-2">
          {[
            { label: 'Envoyée', dot: 'bg-amber-500' },
            { label: 'Acceptée', dot: 'bg-blue-500' },
            { label: 'Terminée', dot: 'bg-emerald-500' },
            { label: 'Expirée', dot: 'bg-red-500' },
          ].map(it => (
            <div key={it.label} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${it.dot}`} />
              {it.label}
            </div>
          ))}
        </div>
      </div>

      <MissionDetailDialog
        mission={selected}
        onClose={() => setSelected(null)}
        backendUrl={process.env.REACT_APP_BACKEND_URL}
      />
    </MainLayout>
  );
};

const MissionDetailDialog = ({ mission, onClose, backendUrl }) => {
  if (!mission) return null;
  const meta = STATUS_META[mission.status] || { label: mission.status, color: 'bg-slate-100 text-slate-700 border-slate-300', dot: 'bg-slate-500' };
  const dt = getEventDate(mission);
  const dtFormatted = dt ? format(dt, "EEEE d MMMM yyyy 'à' HH'h'mm", { locale: fr }) : 'Non planifié';
  const sentTo = Array.isArray(mission.sent_to) ? mission.sent_to : [];
  const cats = Array.isArray(mission.issue_categories) ? mission.issue_categories.join(', ') : '';
  const sevLabel = mission.issue_severity === 'probleme_majeur' ? 'Urgent' : 'Standard';

  return (
    <Dialog open={!!mission} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${meta.color}`}>{meta.label}</span>
            {mission.issue_severity === 'probleme_majeur' && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-red-100 text-red-700 border-red-300">{sevLabel}</span>
            )}
            {cats && (
              <span className="text-xs text-slate-500">· {cats}</span>
            )}
          </DialogTitle>
          <DialogDescription className="pt-2 text-base text-slate-900 font-semibold">
            {mission.property_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {mission.property_address && (
            <Row icon={MapPin} label="Adresse" value={mission.property_address} />
          )}
          <Row icon={Clock} label="Créneau" value={dtFormatted} highlight />

          {sentTo.length > 0 && (
            <div className="flex gap-2.5 items-start">
              <UserIcon size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-[11px] uppercase font-semibold text-slate-400 mb-0.5">Artisan{sentTo.length > 1 ? 's' : ''}</div>
                {sentTo.map((c, i) => (
                  <div key={i} className="text-slate-700">{c.company_name} <span className="text-slate-400">· {c.email}</span></div>
                ))}
              </div>
            </div>
          )}

          {mission.issue_summary && (
            <Row icon={FileText} label="Description" value={mission.issue_summary} />
          )}

          {mission.acceptance_note && (
            <NoteBlock title="Note du prestataire" body={mission.acceptance_note} />
          )}
          {mission.sender_note && (
            <NoteBlock title="Votre note" body={mission.sender_note} />
          )}
          {mission.completion_note && (
            <NoteBlock title="Compte-rendu" body={mission.completion_note} />
          )}
        </div>

        <div className="flex gap-2 pt-2 border-t border-slate-100 mt-2">
          <Button asChild variant="outline" size="sm">
            <a href={`${backendUrl}/api/missions/public/${mission.token}`} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} className="mr-1" /> Page publique
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ icon: Icon, label, value, highlight }) => (
  <div className="flex gap-2.5 items-start">
    <Icon size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
    <div className="flex-1">
      <div className="text-[11px] uppercase font-semibold text-slate-400 mb-0.5">{label}</div>
      <div className={highlight ? 'text-slate-900 font-medium capitalize' : 'text-slate-700 leading-relaxed'}>{value}</div>
    </div>
  </div>
);

const NoteBlock = ({ title, body }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
    <div className="text-[11px] uppercase font-semibold text-slate-400 mb-1">{title}</div>
    <div className="text-slate-700 italic text-xs leading-relaxed">{body}</div>
  </div>
);

export default CalendarPage;
